'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import useLocationStore from '@cineamo/composables/src/stores/location/useLocationStore';

import { LocationResponse } from './user-location-helper';

type GetLocationStoreDataProps = {
    locationAction: () => Promise<LocationResponse>;
};

/**
 * Initializes the location store with the visitor's IP-based geo location (server action).
 *
 * Mirrors the mobile apps' LocationHelper.updateLocationByIP semantics: an IP-based update
 * must never overwrite a position the user obtained via GPS or picked manually.
 */
export default function GetLocationStoreData({ locationAction }: GetLocationStoreDataProps) {
    const { setLocation, setPrecision, setGeoLocation, setLocationInitialized } = useLocationStore();

    const {
        data: locationResponse,
        isSuccess,
        isError
    } = useQuery({
        queryKey: ['location'],
        queryFn: async () => locationAction()
    });

    useEffect(() => {
        if (!locationResponse) {
            return;
        }

        // GPS/MANUAL position present → keep it; the IP position is less precise.
        const { precision } = useLocationStore.getState();
        if (precision === 'GPS' || precision === 'MANUAL') {
            return;
        }

        setGeoLocation(locationResponse.geoLocation ?? undefined);
        if (
            locationResponse.initialLocationStoreState.longitude !== undefined &&
            locationResponse.initialLocationStoreState.latitude !== undefined
        ) {
            setLocation(
                locationResponse.initialLocationStoreState.longitude,
                locationResponse.initialLocationStoreState.latitude
            );
            setPrecision('IP');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationResponse]);

    // Mark location as initialized when query succeeds or fails
    useEffect(() => {
        if (isSuccess || isError) {
            setLocationInitialized(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess, isError]);

    return null;
}
